/**
 * Lift2Fit — coaching workspace: clients (`#/crm/clients`).
 *
 * The single client surface. Phase 1's minimal `#/clients` roster is folded in
 * here (its route now redirects), so there is one roster, one seat gauge and one
 * way into a client. A client is listed only while the link is live; the API
 * masks the rest.
 *
 * The row-level **Review due** badge stays here — it says WHICH client. The full
 * list of what needs doing lives in its own To-do tab (`#/crm/todo`), which is
 * the generic task queue; this screen is not a second copy of it.
 */
import { useMemo, useState } from 'react';
import { api, type CoachRoster, type InternalCoachList, type Trainee } from '@/lib/api';
import { cn, formatCompactCount, formatCount, timeAgo } from '@/lib/format';
import { AlertIcon, ArrowRightIcon, CheckIcon, PlusIcon, SparkIcon, TimerIcon } from '@/lib/icons';
import {
  AdherenceBar,
  AppLinkButton,
  EmptyState,
  FlagChips,
  LoadingPanel,
  MetricTile,
  Panel,
  ScreenHeader,
  StatusChip,
  TrackBadge,
} from '../components';
import { trackMeta } from '../demoData';
import { invalidate } from '@/lib/readCache';
import { useAsync, useCachedAsync } from '../hooks';
import { Link } from '../router';
import { ConnectRequestsPanel } from './ConnectRequestsPanel';
import { CRM_EYEBROW, ListMore, QUEUE_LIST_LIMIT, formatDateShort, planSizeLabel, managedBySentence } from './shared';

/**
 * The words behind the review badge. Colour is the at-a-glance signal; this is
 * the accessible label and the tooltip, so the badge never means colour alone.
 * It is distinct from the red missed-session flags: missed = danger, review due
 * = info (blue).
 */
function reviewDueLabel(client: Trainee): string {
  if (client.daysRemaining === null || client.daysRemaining === undefined) return 'Review due';
  if (client.daysRemaining < 0) return `Review overdue by ${Math.abs(client.daysRemaining)} day(s)`;
  if (client.daysRemaining === 0) return 'Review due — the block ends today';
  return `Review due — block ends in ${client.daysRemaining} day(s)`;
}

/**
 * 0030: who manages this client, in the user's words. A self-managed client
 * says so; a gym-managed client names the manager — the internal coach for the
 * gym owner, or the main gym/coach for the internal coach reading the row.
 */
function managedByLabel(client: Trainee): string | null {
  // 0034: ownership AND delivery, from one shared sentence.
  return managedBySentence(client.managedBy);
}

function SeatGauge({ seats }: { seats: CoachRoster['seats'] }) {
  const limitText = seats.limit === null ? 'unlimited' : String(seats.limit);
  const over = seats.limit !== null && seats.used > seats.limit;
  return (
    <section
      aria-label="Client seats in use"
      data-testid="seat-gauge"
      className="flex flex-col gap-2 border border-hairline bg-surface p-4"
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Clients in use</span>
      <span className="l2f-tabular font-mono text-[26px] font-bold leading-none text-ink">
        {seats.used}
        <span className="text-ink-muted"> / {limitText}</span>
      </span>
      <span className="text-[12px] text-ink-muted">
        {planSizeLabel(seats.plan)} · {seats.active} active · {seats.pending} pending
      </span>
      {over ? (
        <StatusChip tone="warning" icon={<AlertIcon />}>
          Above the current plan size
        </StatusChip>
      ) : seats.limit !== null && seats.remaining === 0 ? (
        <StatusChip tone="neutral">Every place in use</StatusChip>
      ) : (
        <StatusChip tone="success" icon={<CheckIcon />}>
          {seats.remaining === null ? 'Room to grow' : `${seats.remaining} place(s) free`}
        </StatusChip>
      )}
    </section>
  );
}

/**
 * 0030: the gym owner's internal-coach overview. Attached to the Clients page
 * (an existing surface, no new nav tab) so the owner sees each team member's
 * clients, adherence, activity and unread chats beside the roster they manage.
 */
function InternalCoachOverview({ list }: { list: InternalCoachList }) {
  const active = list.data.filter((coach) => coach.status === 'active');
  return (
    <Panel
      title="Your team"
      subtitle="The internal coaches who manage your clients — clients, adherence and activity at a glance."
      bodyClassName="gap-3"
      actions={
        <AppLinkButton to="/crm/profile" variant="outline">
          Manage team
        </AppLinkButton>
      }
    >
      {active.length === 0 ? (
        <p className="text-[13px] text-ink-muted">
          No internal coaches yet. Add one from Profile &amp; settings.
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2" data-testid="crm-team-overview">
          {active.map((coach) => (
            <li
              key={coach.coachId}
              data-testid={`team-coach-${coach.coachId}`}
              className="flex flex-col gap-2 border border-hairline bg-surface p-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[14px] font-bold text-ink">{coach.displayName}</p>
                <StatusChip tone="neutral">
                  {coach.clientsManaged} client{coach.clientsManaged === 1 ? '' : 's'}
                </StatusChip>
              </div>
              <p className="text-[12px] text-ink-muted">{coach.email}</p>
              <dl className="grid grid-cols-3 gap-2">
                <div className="flex flex-col gap-0.5">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                    Adherence
                  </dt>
                  <dd className="l2f-tabular font-mono text-[13px] font-bold text-ink">
                    {coach.adherencePct === null ? '—' : `${coach.adherencePct}%`}
                  </dd>
                </div>
                <div className="flex flex-col gap-0.5">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                    Sessions / 7d
                  </dt>
                  <dd className="l2f-tabular font-mono text-[13px] font-bold text-ink">
                    {coach.sessionsLast7}
                  </dd>
                </div>
                <div className="flex flex-col gap-0.5">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                    Unread chats
                  </dt>
                  <dd className="l2f-tabular font-mono text-[13px] font-bold text-ink">
                    {coach.unreadChats}
                  </dd>
                </div>
              </dl>
              {coach.inactiveClients > 0 ? (
                <p className="text-[11px] text-ink-muted">
                  {coach.inactiveClients} client{coach.inactiveClients === 1 ? '' : 's'} inactive for
                  14+ days
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-ink-muted">
        {list.used}
        {list.limit === null ? '' : ` of ${list.limit}`} internal coach
        {list.limit === 1 ? '' : 'es'} on your plan.
      </p>
    </Panel>
  );
}

export function CrmClientsScreen() {
  const state = useCachedAsync('coaching/clients', () => api.coachClients(), []);
  const reviewsState = useAsync(() => api.crmReviews(), []);
  const requestsState = useAsync(() => api.coachRequests(), []);
  // 0030: the gym owner's team overview, attached to this existing page rather
  // than a new nav tab. An internal coach reading this screen gets no panel.
  const teamState = useCachedAsync('coaching/internal-coaches', () => api.internalCoaches(), []);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const roster = state.data;
  const reviews = reviewsState.data?.data ?? [];
  const requests = requestsState.data?.data ?? [];
  const pendingRequests = requestsState.data?.pending ?? 0;

  const filtered = useMemo(() => {
    if (!roster) return [];
    const needle = query.trim().toLowerCase();
    if (needle === '') return roster.data;
    return roster.data.filter(
      (client) =>
        client.displayName.toLowerCase().includes(needle) || client.email.toLowerCase().includes(needle),
    );
  }, [roster, query]);

  // The roster is the fullest client surface, so it can reveal everyone — but a
  // book of dozens should not bury the filter and the seat gauge under it.
  const visible = showAll ? filtered : filtered.slice(0, QUEUE_LIST_LIMIT);

  if (state.loading && !roster) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading your clients" rows={3} />
        <LoadingPanel label="Loading the seat gauge" rows={4} />
      </div>
    );
  }

  if (!roster) {
    return (
      <EmptyState
        title="We could not read your clients"
        body={state.error ?? 'The API did not answer. Check that you are signed in to a coaching workspace.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/crm" variant="outline">
            Back to the summary
          </AppLinkButton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="crm-clients">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title="Clients"
        badge={
          roster.seats.limit !== null && roster.seats.used > roster.seats.limit ? (
            <StatusChip tone="warning">Above plan size</StatusChip>
          ) : undefined
        }
        lead="Your roster: status, last session, adherence and flags. Open a client to read their programme and prescribe into it."
        actions={
          // 0028 polish: the general chat entry is the icon-only button in the
          // CRM shell header (beside the theme toggle), not a button here. A
          // client's own page keeps the focused per-thread entry.
          <AppLinkButton to="/crm/insights">
            <SparkIcon className="text-[14px]" />
            Open insights
          </AppLinkButton>
        }
      />

      {/* Requests first: a pending connect request is a client waiting at the
          door, so it belongs above the roster and the seat gauge, not behind a
          menu. The panel always renders (with an empty state) so a coach knows
          where to look. */}
      <ConnectRequestsPanel
        requests={requests}
        pending={pendingRequests}
        onChanged={() => {
          // instant paint: a new client or a changed link invalidates the roster.
          invalidate('coaching');
          invalidate('crm');
          requestsState.reload();
          state.reload();
        }}
      />

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <MetricTile
          label="Clients"
          value={formatCompactCount(roster.total)}
          valueTitle={formatCount(roster.total)}
          hint={`${roster.data.filter((client) => client.status === 'active').length} active`}
        />
        <MetricTile
          label="Requests"
          value={formatCompactCount(pendingRequests)}
          valueTitle={formatCount(pendingRequests)}
          hint={pendingRequests ? 'waiting for your answer' : 'nothing waiting'}
          hintTone={pendingRequests ? 'danger' : 'success'}
          icon={pendingRequests ? <TimerIcon /> : <CheckIcon />}
        />
        <MetricTile
          label="Flagged"
          value={formatCompactCount(roster.flagged)}
          valueTitle={formatCount(roster.flagged)}
          hint={roster.flagged ? 'worth a look this week' : 'nothing flagged'}
          hintTone={roster.flagged ? 'danger' : 'success'}
          icon={roster.flagged ? <AlertIcon /> : <CheckIcon />}
        />
        <MetricTile
          label="Reviews due"
          value={formatCompactCount(reviews.length)}
          valueTitle={formatCount(reviews.length)}
          hint={reviews.length ? 'open the To-do tab' : 'nothing to review'}
          hintTone={reviews.length ? 'default' : 'success'}
          icon={reviews.length ? <TimerIcon /> : <CheckIcon />}
        />
        <MetricTile
          label="Shared with you"
          value={formatCompactCount(roster.data.filter((client) => client.sharedFields.length > 0).length)}
          valueTitle={formatCount(roster.data.filter((client) => client.sharedFields.length > 0).length)}
          hint="of their own fields"
        />
        <SeatGauge seats={roster.seats} />
      </section>

      {/* 0030: the owner's team overview sits above the roster. An internal
          coach reading this screen has no team of their own, so no panel. */}
      {roster.internalCoachOf === null && teamState.data ? (
        <InternalCoachOverview list={teamState.data} />
      ) : null}

      <Panel
        title="Roster"
        subtitle="Flagged first. Opening a client reads only the fields they share, while the link is live."
        bodyClassName="gap-3"
        actions={
          <label className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Filter</span>
            <input
              type="search"
              data-testid="crm-client-filter"
              value={query}
              placeholder="Name or email"
              onChange={(event) => setQuery(event.target.value)}
              className="h-9 w-44 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </label>
        }
      >
        {filtered.length ? (
          <ul className="flex flex-col gap-3">
            {visible.map((client) => {
              const meta = trackMeta(client.track);
              return (
                <li key={client.userId} data-testid={`client-row-${client.userId}`}>
                  <Link
                    to={`/crm/clients/${client.userId}`}
                    className="flex flex-col gap-3 border border-hairline bg-surface p-4 transition-colors duration-150 hover:border-accent"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-col gap-1">
                        <p className="flex flex-wrap items-center gap-2 text-[15px] font-bold text-ink">
                          {client.displayName}
                          {client.status === 'invited' ? <StatusChip tone="info">Invited</StatusChip> : null}
                          {client.status === 'revoked' ? <StatusChip tone="neutral">Former client</StatusChip> : null}
                        </p>
                        <p className="text-[13px] text-ink-muted">
                          {meta.label} · {client.email || 'no email'}
                        </p>
                        {managedByLabel(client) ? (
                          <p
                            className="text-[12px] font-bold uppercase tracking-[0.08em] text-accent"
                            data-testid={`client-manager-${client.userId}`}
                          >
                            {managedByLabel(client)}
                          </p>
                        ) : null}
                        {client.blockName && client.blockEndsOn ? (
                          <p className="text-[12px] text-ink-muted">
                            Block: {client.blockName} · week {client.weekOf ?? '—'} of {client.weeks ?? '—'} · ends{' '}
                            {formatDateShort(client.blockEndsOn)}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {client.reviewDue ? (
                          <span
                            data-testid={`client-review-badge-${client.userId}`}
                            aria-label={reviewDueLabel(client)}
                            title={reviewDueLabel(client)}
                          >
                            <StatusChip tone="info" icon={<TimerIcon />}>
                              Review due
                            </StatusChip>
                          </span>
                        ) : null}
                        <TrackBadge track={client.track} />
                        <FlagChips flags={client.flags} />
                      </div>
                    </div>

                    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <div className="flex flex-col gap-1">
                        <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Adherence</dt>
                        <dd>
                          <AdherenceBar pct={client.compliancePct} />
                        </dd>
                      </div>
                      <div className="flex flex-col gap-1">
                        <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Last session</dt>
                        <dd className="text-[13px] text-ink">
                          {client.lastSessionAt ? timeAgo(client.lastSessionAt) : 'Never logged'}
                        </dd>
                      </div>
                      <div className="flex flex-col gap-1">
                        <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Sessions / 7d</dt>
                        <dd className="l2f-tabular font-mono text-[13px] font-bold text-ink">{client.sessionsLast7}</dd>
                      </div>
                      <div className="flex flex-col gap-1">
                        <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Shares</dt>
                        <dd className="text-[13px] text-ink">{client.sharedFields.join(', ') || 'nothing'}</dd>
                      </div>
                    </dl>
                    <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-accent">
                      Open client
                      <ArrowRightIcon />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : query.trim() !== '' ? (
          <EmptyState
            title="No client matches that filter"
            body="Try another name or email address."
            icon={<AlertIcon className="h-8 w-8 text-accent" />}
          />
        ) : (
          <EmptyState
            title="No clients yet"
            body="Invite a client from the coaching flow. Once they accept, their shared training appears here — you see only what they choose to share, and only while the link is live."
            icon={<SparkIcon className="h-8 w-8 text-accent" />}
            action={
              <AppLinkButton to="/coach" variant="outline">
                <PlusIcon />
                Invite a client
              </AppLinkButton>
            }
          />
        )}
        <ListMore
          total={filtered.length}
          shown={visible.length}
          onShowAll={() => setShowAll(true)}
          toLabel="Show all"
          testId="crm-clients-more"
        />
      </Panel>

      <p className={cn('text-[12px] text-ink-muted')}>
        Revoked links appear as “Former client”. A client can revoke at any time — access stops on the very next request.
      </p>
    </div>
  );
}
